<script setup lang="ts">
import { Deferred, Head, Link, router, useForm } from "@inertiajs/vue3"
import { Check, Copy, FileText, SlidersHorizontal, X } from "lucide-vue-next"
import { computed, ref, watch } from "vue"

import AddressForm, { type AddressFields } from "@/components/AddressForm.vue"
import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppPagination from "@/components/AppPagination.vue"
import FormSection from "@/components/FormSection.vue"
import HeadingSmall from "@/components/HeadingSmall.vue"
import InputError from "@/components/InputError.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import StatusBadge from "@/components/applications/StatusBadge.vue"
import { type Comment } from "@/components/comments/CommentItem.vue"
import CommentThread from "@/components/comments/CommentThread.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { useCopyToClipboard } from "@/composables/useCopyToClipboard"
import AppLayout from "@/layouts/AppLayout.vue"
import PartnerLayout from "@/layouts/partners/Layout.vue"
import { APPLICATION_STATUSES } from "@/lib/constants"
import { formatAmount, formatLabel } from "@/lib/utils"
import {
  editLoanApplicationPath,
  editPartnerPath,
  partnerAddressPath,
  partnerCommentsPath,
  partnerPath,
  partnersPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Organization {
  id: number
  name: string
}

interface Partner {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
  partner_type: string | null
  categories: string[] | null
  organization: Organization
}

interface Address {
  id?: number
  unit_no: string | null
  street_no: string
  street_name: string
  street_type: string
  city: string
  state: string
  postcode: string
}

interface Application {
  id: string
  status: string
  status_at: string | null
  rejection_reason: string | null
  loan_type: string
  purpose: string
  amount: number
  created_at: string
  completed_in_full: boolean
}

interface AppFilters {
  status?: string | null
  loan_type?: string | null
}

const props = defineProps<{
  partner: Partner
  organizations: Organization[]
  address?: Address | null
  applications?: { records: Application[]; pagination: PaginationMeta; filters: AppFilters } | null
  comments?: Comment[] | null
  loan_types: string[]
  partner_categories: Record<string, string[]>
  partner_types: string[]
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Partners", href: partnersPath() },
  {
    title: props.partner.name,
    href: editPartnerPath(props.partner.id),
  },
]

const urlSection = new URLSearchParams(window.location.search).get("section")
const validSections = ["details", "address", "applications"] as const
type Section = (typeof validSections)[number]
const activeSection = ref<Section>(
  validSections.includes(urlSection as Section) ? (urlSection as Section) : "details",
)

const detailsForm = useForm({
  name: props.partner.name,
  contact: props.partner.contact,
  email: props.partner.email,
  phone: props.partner.phone,
  active: props.partner.active,
  organization_id: props.partner.organization.id as string | number,
  partner_type: props.partner.partner_type ?? "",
  categories: (props.partner.categories ?? []) as string[],
})

watch(
  () => detailsForm.partner_type,
  () => { detailsForm.categories = [] },
)

function toggleDetailCategory(cat: string, checked: boolean) {
  if (checked) {
    detailsForm.categories = [...detailsForm.categories, cat]
  } else {
    detailsForm.categories = detailsForm.categories.filter((c) => c !== cat)
  }
}

const availableCategories = computed(() => props.partner_categories[detailsForm.partner_type] ?? [])

const { copy, isCopied } = useCopyToClipboard()

const partnerFormUrl = computed(() => {
  const base = window.location.origin
  const type = props.partner.partner_type ?? ""
  return `${base}/apply?partner_id=${props.partner.id}${type ? `&type=${type}` : ""}`
})

function submitDetails() {
  detailsForm.patch(partnerPath(props.partner.id), {
    preserveScroll: true,
  })
}

const addressForm = useForm({
  unit_no: "",
  street_no: "",
  street_name: "",
  street_type: "",
  city: "",
  state: "",
  postcode: "",
})

watch(
  () => props.address,
  (addr) => {
    if (!addr) return
    Object.assign(addressForm, {
      unit_no: addr.unit_no ?? "",
      street_no: addr.street_no,
      street_name: addr.street_name,
      street_type: addr.street_type,
      city: addr.city,
      state: addr.state,
      postcode: addr.postcode,
    })
  },
  { immediate: true },
)

const addressFields = computed<AddressFields>({
  get: () => ({
    unit_no: addressForm.unit_no,
    street_no: addressForm.street_no,
    street_name: addressForm.street_name,
    street_type: addressForm.street_type,
    city: addressForm.city,
    state: addressForm.state,
    postcode: addressForm.postcode,
  }),
  set: (val) => Object.assign(addressForm, val),
})

function submitAddress() {
  addressForm.patch(
    partnerAddressPath(props.partner.id),
    { preserveScroll: true },
  )
}


// ── Applications filters ───────────────────────────────────────────────────
const ALL = "__all__"
const urlParams = new URLSearchParams(window.location.search)

const appStatusFilter = ref(urlParams.get("status") ?? ALL)
const appLoanTypeFilter = ref(urlParams.get("loan_type") ?? ALL)

const hasAppFilters = computed(
  () => appStatusFilter.value !== ALL || appLoanTypeFilter.value !== ALL,
)

const appFilterParams = computed(() => ({
  section: "applications",
  status: appStatusFilter.value !== ALL ? appStatusFilter.value : undefined,
  loan_type: appLoanTypeFilter.value !== ALL ? appLoanTypeFilter.value : undefined,
}))

function applyAppFilters() {
  const params = Object.fromEntries(
    Object.entries(appFilterParams.value).filter(([, v]) => v != null),
  )
  router.get(editPartnerPath(props.partner.id), params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

watch([appStatusFilter, appLoanTypeFilter], applyAppFilters)

function clearAppFilters() {
  appStatusFilter.value = ALL
  appLoanTypeFilter.value = ALL
}

const appTableColumns: TableColumn[] = [
  { key: "purpose", label: "Purpose" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
  { key: "complete", label: "Complete" },
  { key: "date", label: "Date" },
  { key: "actions", label: "", align: "right" },
]
</script>

<template>
  <Head :title="`Edit ${partner.name}`" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <PartnerLayout
      v-model:active-section="activeSection"
      :partner-id="partner.id"
      :partner-name="partner.name"
      :organization-name="partner.organization.name"
    >
      <!-- ── Details section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'details'" class="space-y-6">
        <HeadingSmall
          title="Partner details"
          description="Update the partner's name and contact information"
        />

        <form class="space-y-4" @submit.prevent="submitDetails">
          <FormSection title="Contact Information">
            <div class="grid gap-2">
              <Label for="name">Name</Label>
              <Input id="name" v-model="detailsForm.name" placeholder="John Smith" required />
              <InputError :messages="detailsForm.errors.name" />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="grid gap-2">
                <Label for="contact">Contact Person</Label>
                <Input id="contact" v-model="detailsForm.contact" placeholder="John Smith" required />
                <InputError :messages="detailsForm.errors.contact" />
              </div>
              <div class="grid gap-2">
                <Label for="phone">Phone</Label>
                <Input id="phone" v-model="detailsForm.phone" type="tel" placeholder="+61 400 000 000" required />
                <InputError :messages="detailsForm.errors.phone" />
              </div>
            </div>

            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input id="email" v-model="detailsForm.email" type="email" placeholder="john@example.com" required />
              <InputError :messages="detailsForm.errors.email" />
            </div>

            <div class="grid gap-2">
              <Label for="organization">Organization</Label>
              <Select v-model="detailsForm.organization_id">
                <SelectTrigger class="w-full" id="organization">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="org in organizations"
                    :key="org.id"
                    :value="org.id"
                  >{{ org.name }}</SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="detailsForm.errors.organization_id" />
            </div>
          </FormSection>

          <FormSection title="Service Type">

            <div class="grid gap-2">
              <Label for="partner-type">Type</Label>
              <Select v-model="detailsForm.partner_type">
                <SelectTrigger class="w-full" id="partner-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="t in props.partner_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="detailsForm.errors.partner_type" />
            </div>

            <div v-if="availableCategories.length > 0" class="grid gap-2">
              <Label>Categories</Label>
              <p class="text-muted-foreground text-xs">Select all service categories this partner offers.</p>
              <div class="grid grid-cols-2 gap-y-2.5 gap-x-4">
                <div
                  v-for="cat in availableCategories"
                  :key="cat"
                  class="flex items-center gap-2"
                >
                  <Checkbox
                    :id="`cat-${cat}`"
                    :model-value="detailsForm.categories.includes(cat)"
                    @update:model-value="toggleDetailCategory(cat, $event as boolean)"
                  />
                  <Label :for="`cat-${cat}`" class="font-normal">{{ formatLabel(cat) }}</Label>
                </div>
              </div>
              <InputError :messages="detailsForm.errors.categories" />
            </div>
          </FormSection>

          <FormSection title="Application Link">
            <p class="text-muted-foreground text-xs">Share this URL with the partner to let customers apply directly.</p>
            <div class="flex items-center gap-2">
              <Input :model-value="partnerFormUrl" readonly class="text-muted-foreground font-mono text-xs" />
              <Button
                type="button"
                variant="outline"
                size="icon"
                class="shrink-0"
                @click="copy(partnerFormUrl, 'partner-url')"
              >
                <Check v-if="isCopied('partner-url')" class="size-4 text-emerald-500" />
                <Copy v-else class="size-4" />
              </Button>
            </div>
          </FormSection>

          <div class="border-border flex items-center justify-between rounded-lg border p-4">
            <div>
              <p class="text-sm font-medium">Active</p>
              <p class="text-muted-foreground text-xs">Allow this partner to submit loan applications.</p>
            </div>
            <Switch id="active" v-model="detailsForm.active" />
          </div>

          <div class="flex items-center gap-4 pt-2">
            <Button type="submit" :disabled="detailsForm.processing">Save Changes</Button>
            <Transition
              enter-active-class="transition ease-in-out"
              enter-from-class="opacity-0"
              leave-active-class="transition ease-in-out"
              leave-to-class="opacity-0"
            >
              <p v-show="detailsForm.recentlySuccessful" class="text-muted-foreground text-sm">
                Saved.
              </p>
            </Transition>
          </div>
        </form>
      </div>

      <!-- ── Address section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'address'" class="space-y-6">
        <HeadingSmall
          title="Address"
          description="Update the partner's physical address"
        />

        <Deferred data="address">
          <template #fallback>
            <div class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
              </div>
              <Skeleton class="h-9" />
              <div class="grid grid-cols-3 gap-4">
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
              </div>
            </div>
          </template>

          <form class="space-y-4" @submit.prevent="submitAddress">
            <FormSection title="Address">
              <AddressForm v-model="addressFields" :errors="addressForm.errors" />
            </FormSection>

            <div class="flex items-center gap-4 pt-2">
              <Button type="submit" :disabled="addressForm.processing">Save Address</Button>
              <Transition
                enter-active-class="transition ease-in-out"
                enter-from-class="opacity-0"
                leave-active-class="transition ease-in-out"
                leave-to-class="opacity-0"
              >
                <p v-show="addressForm.recentlySuccessful" class="text-muted-foreground text-sm">
                  Saved.
                </p>
              </Transition>
            </div>
          </form>
        </Deferred>
      </div>

      <!-- ── Applications section ──────────────────────────────────────── -->
      <div v-show="activeSection === 'applications'" class="space-y-4">
        <HeadingSmall
          title="Applications"
          description="All loan applications submitted through this partner"
        />

        <!-- Filters -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select v-model="appStatusFilter">
            <SelectTrigger class="w-full sm:w-40">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any status</SelectItem>
              <SelectItem v-for="s in APPLICATION_STATUSES" :key="s" :value="s">{{ formatLabel(s) }}</SelectItem>
            </SelectContent>
          </Select>
          <Select v-model="appLoanTypeFilter">
            <SelectTrigger class="w-full sm:w-40">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any type</SelectItem>
              <SelectItem v-for="t in props.loan_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
            </SelectContent>
          </Select>
          <Button v-if="hasAppFilters" variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground shrink-0" @click="clearAppFilters">
            <X class="size-4" />
            Clear
          </Button>
        </div>

        <Deferred data="applications">
          <template #fallback>
            <div class="space-y-2">
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
            </div>
          </template>

          <div v-if="!applications || applications.records.length === 0" class="flex flex-col items-center gap-3 py-10 text-center">
            <FileText class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasAppFilters ? "No applications match your filters" : "No applications yet" }}
            </p>
            <Button v-if="hasAppFilters" variant="outline" size="sm" @click="clearAppFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <div v-else class="rounded-lg border">
            <div class="border-border flex items-center justify-between border-b px-4 py-2">
              <span class="text-muted-foreground text-xs">
                {{ applications.pagination.total_count }} application{{ applications.pagination.total_count !== 1 ? "s" : "" }}
                {{ hasAppFilters ? "found" : "total" }}
              </span>
              <SlidersHorizontal class="text-muted-foreground size-3.5" />
            </div>
            <AppDataTable :columns="appTableColumns" compact>
              <tr
                v-for="app in applications.records"
                :key="app.id"
                class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
              >
                <td class="px-4 py-3 font-medium">{{ formatLabel(app.purpose) }}</td>
                <td class="text-muted-foreground px-4 py-3">{{ formatAmount(app.amount) }}</td>
                <td class="px-4 py-3"><StatusBadge :status="app.status" :status-at="app.status_at" :rejection-reason="app.rejection_reason" /></td>
                <td class="px-4 py-3">
                  <Badge :variant="app.completed_in_full ? 'default' : 'secondary'">
                    {{ app.completed_in_full ? "Yes" : "No" }}
                  </Badge>
                </td>
                <td class="text-muted-foreground px-4 py-3"><RelativeTime :date="app.created_at" /></td>
                <td class="px-4 py-3 text-right">
                  <Link :href="editLoanApplicationPath(app.id)" class="text-primary text-xs hover:underline">Open →</Link>
                </td>
              </tr>
            </AppDataTable>
            <AppPagination
              :current-page="applications.pagination.current_page"
              :total-pages="applications.pagination.total_pages"
              :total-count="applications.pagination.total_count"
              :per-page="applications.pagination.per_page"
              :base-url="editPartnerPath(partner.id)"
              :extra-params="appFilterParams"
            />
          </div>
        </Deferred>
      </div>
    </PartnerLayout>

    <!-- Activity & Comments — shown under details section -->
    <div v-if="activeSection === 'details'" class="flex max-w-7xl flex-col px-4 pb-6 lg:flex-row lg:space-x-12">
      <div class="hidden lg:block lg:w-48 lg:shrink-0" />
      <div class="flex-1">
        <Deferred data="comments">
          <template #fallback>
            <div class="mt-8 space-y-2">
              <div class="bg-muted h-4 w-48 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
            </div>
          </template>
          <CommentThread
            :comments="comments ?? []"
            :create-path="partnerCommentsPath(partner.id)"
            :can-comment="true"
          />
        </Deferred>
      </div>
    </div>
  </AppLayout>
</template>
